/* ── WORK_TRACK — Team ────────────────────────────────────── */

import {
  $,
  $$,
  esc,
  icon,
  api,
  state,
  toast,
  registerView,
  loadMembers,
  loadingHtml,
  emptyHtml,
  avatarHtml,
  fmtMin,
  todayStr,
  daysAgoStr,
  ROLE_LABEL
} from './core';
import type { Invite, Member, Role } from './types';

interface TeamResponse {
  members: Member[];
  invites: Invite[];
}

interface TeamTimeProject {
  project_id: number | null;
  name: string;
  color: string | null;
  icon: string | null;
  minutes: number;
}

interface TeamTimeMember {
  id: number;
  username: string;
  name: string | null;
  avatar: string | null;
  role: string;
  today_minutes: number;
  week_minutes: number;
  month_minutes: number;
  total_minutes: number;
  daily: { date: string; minutes: number }[];
  per_project: TeamTimeProject[];
}

interface TeamEntry {
  id: number;
  user_id: number;
  username: string;
  user_name: string | null;
  user_avatar: string | null;
  project_name: string | null;
  project_color: string | null;
  task_title: string | null;
  minutes: number;
  note: string | null;
  entry_date: string;
}

export async function loadTeam(): Promise<void> {
  try {
    const data = await api<TeamResponse>('/api/team');
    renderMembers(data.members || []);
    renderInvites(data.invites || []);
    fillEntryUserFilter(data.members || []);
  } catch (e) {
    toast((e as Error).message, true);
  }
  loadTeamTime();
  loadTeamEntries();
}

// ── Team time (owner/admin) ────────────────────────────
export async function loadTeamTime(): Promise<void> {
  const ul = $('#teamTimeList');
  if (!ul) return;
  const rangeSel = $<HTMLSelectElement>('#teamTimeRange');
  const days = Number(rangeSel?.value || 14);
  ul.innerHTML = loadingHtml();
  try {
    const data = await api<TeamTimeMember[]>(`/api/team/time?days=${days}`);
    renderTeamTime(data, days);
  } catch (e) {
    ul.innerHTML = `<li class="empty">${esc((e as Error).message)}</li>`;
  }
}

function renderTeamTime(members: TeamTimeMember[], days: number): void {
  const ul = $('#teamTimeList');
  if (!members.length) {
    ul.innerHTML = emptyHtml(
      icon('users', 30),
      'No members yet',
      'Invite someone to start tracking team time.'
    );
    return;
  }
  const maxDay = Math.max(...members.flatMap(m => m.daily.map(d => d.minutes)), 1);
  ul.innerHTML = members
    .map(m => {
      const initial = (m.name || m.username || '').slice(0, 2).toUpperCase();
      const bars = m.daily
        .map(
          d =>
            `<span class="tt-bar" style="height:${Math.max(3, Math.round((d.minutes / maxDay) * 40))}px" title="${d.date}: ${fmtMin(d.minutes)}"></span>`
        )
        .join('');
      const projects = m.per_project.length
        ? m.per_project
            .map(
              p =>
                `<div class="tt-proj-row">
                   <span class="tt-proj-dot" style="background:${esc(p.color || '#565051')}"></span>
                   <span class="tt-proj-name">${esc(p.name)}</span>
                   <b>${fmtMin(p.minutes)}</b>
                 </div>`
            )
            .join('')
        : '<div class="tt-proj-row tt-proj-empty">No project time in this range.</div>';
      return `<li class="team-time-row">
        ${avatarHtml(initial, m.id, m.avatar || null)}
        <div class="tt-member">
          <div class="item-title">@${esc(m.username)} <span class="role-chip">${esc(ROLE_LABEL[m.role as Role] || m.role)}</span></div>
          <div class="tt-bars" title="Daily minutes (last ${days} days)">${bars}</div>
        </div>
        <div class="tt-stats">
          <span class="tt-stat"><small>Today</small><b>${fmtMin(m.today_minutes)}</b></span>
          <span class="tt-stat"><small>Week</small><b>${fmtMin(m.week_minutes)}</b></span>
          <span class="tt-stat"><small>Month</small><b>${fmtMin(m.month_minutes)}</b></span>
          <span class="tt-stat"><small>Total</small><b>${fmtMin(m.total_minutes)}</b></span>
        </div>
        <button class="tt-projects-toggle" data-proj-toggle="${m.id}" aria-expanded="false" title="Projects">${icon('chevronDown', 15)}</button>
        <div class="tt-projects hidden" data-proj-list="${m.id}">${projects}</div>
      </li>`;
    })
    .join('');

  $$<HTMLButtonElement>('#teamTimeList [data-proj-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const list = document.querySelector<HTMLElement>(`[data-proj-list="${btn.dataset.projToggle}"]`);
      if (!list) return;
      const open = list.classList.toggle('hidden');
      btn.classList.toggle('open', !open);
      btn.setAttribute('aria-expanded', String(!open));
    });
  });
}

const teamTimeRange = $('#teamTimeRange');
if (teamTimeRange) teamTimeRange.addEventListener('change', loadTeamTime);

// ── Team entries (owner/admin): detailed log ───────────
function fillEntryUserFilter(members: Member[]): void {
  const sel = $<HTMLSelectElement>('#teamEntriesUser');
  if (!sel) return;
  sel.innerHTML =
    '<option value="">All members</option>' +
    members
      .map(m => `<option value="${m.id}">@${esc(m.username)}</option>`)
      .join('');
}

export async function loadTeamEntries(): Promise<void> {
  const ul = $('#teamEntriesList');
  if (!ul) return;
  const userId = $<HTMLSelectElement>('#teamEntriesUser')?.value || '';
  const from = $<HTMLInputElement>('#teamEntriesFrom')?.value || daysAgoStr(13);
  const to = $<HTMLInputElement>('#teamEntriesTo')?.value || todayStr();
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  ul.innerHTML = loadingHtml();
  try {
    const entries = await api<TeamEntry[]>(`/api/team/entries?${params.toString()}`);
    renderTeamEntries(entries);
  } catch (e) {
    ul.innerHTML = `<li class="empty">${esc((e as Error).message)}</li>`;
  }
}

function renderTeamEntries(entries: TeamEntry[]): void {
  const ul = $('#teamEntriesList');
  if (!entries.length) {
    ul.innerHTML = emptyHtml(
      icon('clipboard', 30),
      'No entries found',
      'Try widening the date range or choosing another member.'
    );
    return;
  }
  ul.innerHTML = entries
    .map(e => {
      const who = e.user_name || e.username;
      return `<li class="team-entry-row">
        ${avatarHtml((who || '?').slice(0, 2).toUpperCase(), e.user_id, e.user_avatar || null)}
        <div class="item-main">
          <div class="item-title">@${esc(e.username)} ${e.project_name ? '· ' + esc(e.project_name) : ''}${e.task_title ? ' · ' + esc(e.task_title) : ''}</div>
          <div class="item-sub">${esc(e.entry_date)}${e.note ? ' — ' + esc(e.note) : ''}</div>
        </div>
        <div class="item-right"><span class="item-val">${fmtMin(e.minutes)}</span></div>
      </li>`;
    })
    .join('');
}

const teamEntriesUser = $('#teamEntriesUser');
if (teamEntriesUser) teamEntriesUser.addEventListener('change', loadTeamEntries);
const teamEntriesFrom = $('#teamEntriesFrom');
if (teamEntriesFrom) teamEntriesFrom.addEventListener('change', loadTeamEntries);
const teamEntriesTo = $('#teamEntriesTo');
if (teamEntriesTo) teamEntriesTo.addEventListener('change', loadTeamEntries);

function renderMembers(members: Member[]): void {
  $('#memberCount').textContent = `${members.length} members`;
  const ul = $('#membersList');
  if (!members.length) {
    ul.innerHTML = emptyHtml(
      icon('users', 30),
      'No members yet',
      'Invite someone with a code to build out your team.',
      { label: 'Generate invite', on: 'team' }
    );
    return;
  }
  const isOwner = state.user && state.user.role === 'owner';
  ul.innerHTML = members
    .map(m => {
      const initials = m.name || m.username;
      return `<li>
      ${avatarHtml(initials.slice(0, 2).toUpperCase(), m.id, m.avatar || null)}
      <div class="item-main">
        <div class="item-title">@${esc(m.username)} <span class="role-chip">${esc(ROLE_LABEL[m.role] || m.role)}</span></div>
        <div class="item-sub">${esc(m.name || m.username)}${m.email ? ' · ' + esc(m.email) : ''}${m.status === 'disabled' ? ' · disabled' : ''}</div>
      </div>
      ${
        isOwner && m.role !== 'owner'
          ? `
        <div class="item-right">
          <select class="member-role" data-role-select="${m.id}">
            ${['developer', 'admin'].map(r => `<option value="${r}" ${m.role === r ? 'selected' : ''}>${ROLE_LABEL[r as 'developer' | 'admin']}</option>`).join('')}
          </select>
          <button class="member-disable btn btn-ghost btn-sm ${m.status === 'disabled' ? 'member-enable' : ''}" data-mstatus="${m.id}" data-status="${m.status === 'disabled' ? 'disabled' : 'active'}">${m.status === 'disabled' ? 'Enable' : 'Disable'}</button>
          <button class="member-pw btn btn-ghost btn-sm" data-pw="${m.id}" title="Reset password">${icon('key', 14)} Reset</button>
          <button class="member-remove btn btn-danger btn-sm" data-remove="${m.id}">Remove</button>
        </div>`
          : ''
      }
    </li>`;
    })
    .join('');

  $$<HTMLSelectElement>('#membersList [data-role-select]').forEach(sel => {
    sel.addEventListener('change', async () => {
      try {
        await api(`/api/team/members/${sel.dataset.roleSelect}/role`, 'PUT', { role: sel.value });
        toast('Role updated');
      } catch (err) {
        toast((err as Error).message, true);
      }
    });
  });
  $$<HTMLButtonElement>('#membersList [data-mstatus]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const disabled = btn.dataset.status === 'disabled';
      const next = disabled ? 'active' : 'disabled';
      if (disabled) {
        if (!confirm('Enable this member? They will be able to sign in again.')) return;
      } else {
        if (!confirm('Disable this member? They will no longer be able to sign in.')) return;
      }
      try {
        await api(`/api/team/members/${btn.dataset.mstatus}/status`, 'PUT', { status: next });
        toast(disabled ? 'Member enabled' : 'Member disabled');
        loadTeam();
        loadMembers(true);
      } catch (err) {
        toast((err as Error).message, true);
      }
    });
  });
  $$<HTMLButtonElement>('#membersList [data-remove]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (
        !confirm(
          'Permanently remove this member? Their account, chats, comments, and time entries will be deleted. This cannot be undone.'
        )
      )
        return;
      try {
        await api(`/api/team/members/${btn.dataset.remove}/remove`, 'DELETE');
        toast('Member removed');
        loadTeam();
        loadMembers(true);
      } catch (err) {
        toast((err as Error).message, true);
      }
    });
  });
  $$<HTMLButtonElement>('#membersList [data-pw]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const pw = prompt('Enter a new password for this member (at least 8 characters):');
      if (!pw) return;
      if (pw.length < 8) {
        toast('Password must be at least 8 characters', true);
        return;
      }
      if (!confirm('Set this new password? The member must sign in with it next time.')) return;
      try {
        await api(`/api/team/members/${btn.dataset.pw}/password`, 'PUT', { password: pw });
        toast('Password reset');
      } catch (err) {
        toast((err as Error).message, true);
      }
    });
  });
}

function renderInvites(invites: Invite[]): void {
  const ul = $('#invitesList');
  if (!invites.length) {
    ul.innerHTML = emptyHtml(
      icon('mail', 30),
      'No pending invites',
      'Generate invite codes above so teammates can join.'
    );
    return;
  }
  ul.innerHTML = invites
    .filter(i => !i.used)
    .map(
      i => `
    <li>
      <span class="item-icon">${icon('mail', 18)}</span>
      <div class="item-main">
        <div class="item-title">${esc(i.email)} <span class="role-chip">${esc(ROLE_LABEL[i.role] || i.role)}</span></div>
        <div class="item-sub">Expires ${esc(new Date(i.expires_at).toLocaleDateString())}</div>
        <div class="invite-code">Invite code: <code>${esc(i.token)}</code></div>
      </div>
      <div class="item-right tc-actions">
        <button class="invite-copy" data-itoken="${esc(i.token)}" title="Copy code">${icon('copy', 15)}</button>
        <button class="invite-revoke" data-iid="${i.id}" title="Revoke">${icon('x', 14)}</button>
      </div>
    </li>`
    )
    .join('');

  $$<HTMLButtonElement>('#invitesList [data-itoken]').forEach(btn => {
    btn.addEventListener('click', () => {
      navigator.clipboard.writeText(btn.dataset.itoken || '').then(
        () => toast('Invite code copied'),
        () => toast('Copy failed', true)
      );
    });
  });
  $$<HTMLButtonElement>('#invitesList [data-iid]').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!confirm('Revoke this invite?')) return;
      try {
        await api(`/api/team/invites/${btn.dataset.iid}`, 'DELETE');
        toast('Invite revoked');
        loadTeam();
      } catch (err) {
        toast((err as Error).message, true);
      }
    });
  });
}

$<HTMLFormElement>('#inviteForm').addEventListener('submit', async e => {
  e.preventDefault();
  const f = new FormData($<HTMLFormElement>('#inviteForm'));
  const email = String(f.get('email') || '').trim();
  if (!email) return;
  try {
    const invite = await api<Invite & { email_sent: boolean }>('/api/team/invites', 'POST', {
      email,
      role: String(f.get('role') || 'developer'),
      name: f.get('name') ? String(f.get('name')) : undefined
    });
    const link = `${location.origin}${location.pathname}?invite=${encodeURIComponent(invite.token)}`;
    navigator.clipboard.writeText(link).then(
      () => {},
      () => {}
    );
    toast(
      invite.email_sent
        ? 'Invite email sent — sign-up link also copied'
        : 'Invite created & copied (email could not be sent)',
      invite.email_sent ? false : true
    );
    $<HTMLFormElement>('#inviteForm').reset();
    loadTeam();
  } catch (err) {
    toast((err as Error).message, true);
  }
});

registerView('team', loadTeam);
